import React, { useCallback, useMemo } from 'react';
import clsx from 'classnames';
import { SurveyTheme } from '@sm/webassets/dist/components/SurveyTheme/types';
import { createSurveyStyles } from '@sm/webassets';
import { SurveyFormatViewProps } from '../types';
import { findFirstFocusable, isChildFunction } from '../utils';
import useFormatView from './useFormatView';
import { deferToAfterFocusChanged } from '~app/pages/SurveyTaking/validation/delayedValidation';
import { useAppDispatch } from '~app/hooks';
import { setActive, scrollToActive, setNextActive } from '~app/components/Survey/SurveyFormat/surveyFormatSlice';

const useStyles = createSurveyStyles<'shield', { active: boolean; noShield: boolean; autoPadding: boolean }>(
  (theme: SurveyTheme) => ({
    shield: {
      opacity: ({ active, noShield }) => (!!noShield || active ? 1 : 0.5),
      paddingBottom: ({ autoPadding }) => (autoPadding ? '80px' : 0),
      [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
        paddingBottom: ({ autoPadding }) => (autoPadding ? '270px' : 0),
      },
    },
  })
);

export function OQAATFormatView({
  name,
  noShield = false,
  autoPadding = true,
  className,
  children,
  ...props
}: SurveyFormatViewProps): React.ReactElement {
  const dispatch = useAppDispatch();

  const { isActive } = useFormatView(name);

  const { shield } = useStyles({ active: isActive, noShield, autoPadding });

  const handleShieldClick = useCallback(() => {
    const el = document.getElementById(`view-${name}`);
    if (!isActive && el) {
      const firstFocusable = findFirstFocusable(el);
      firstFocusable?.focus({ preventScroll: true });
    }
  }, [isActive, name]);

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      const el = document.getElementById(`view-${name}`);
      if (isActive || el === e.target || !el?.contains(e.target)) {
        return;
      }

      if (noShield) {
        // defer to make done with error-change click work
        deferToAfterFocusChanged(() => {
          dispatch(setActive(name));
          dispatch(scrollToActive(name));
        });
        return;
      }
      dispatch(setActive(name));
      dispatch(scrollToActive(name));
    },
    [name, isActive, noShield, dispatch]
  );

  // only re-render children if their active state changes
  const content = useMemo(() => {
    if (isChildFunction(children)) {
      return children(isActive, () => dispatch(setNextActive()));
    }
    if (children) {
      return React.cloneElement(children, { active: isActive, onSubmit: () => dispatch(setNextActive()) });
    }
    return null;
  }, [children, dispatch, isActive]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      onFocus={handleFocus}
      onClick={noShield ? undefined : handleShieldClick}
      // `offset-target-container` is needed for apps/respweb/views/spa-survey.hbs to calculate initial `marginTop`
      className={clsx('offset-target-container', shield, className)}
      {...props}
      id={`view-${name}`}
      data-testid={`OQAATView__${name}`}
    >
      {content}
    </div>
  );
}

export default OQAATFormatView;
